import type { AuthenticationClient, RestClient } from '@directus/sdk'

import {
  clearNuxtData,
  navigateTo,
  useNuxtApp,
  useRoute,
  useRuntimeConfig,
  useState,
} from '#imports'
import { readMe } from '@directus/sdk'
import { jwtDecode } from 'jwt-decode'

import type { User } from '~/types'
import type { Schema } from '~/types/schema'

export default function useDirectusAuth<DirectusSchema extends object>() {
  const nuxtApp = useNuxtApp()
  const $directus = nuxtApp.$directus as RestClient<Schema> &
    AuthenticationClient<Schema>

  const user: Ref<User | null | undefined> = useState('user')

  const config = useRuntimeConfig()

  const _loggedIn = {
    get: () => process.client && localStorage.getItem('authenticated'),
    set: (value: boolean) =>
      process.client && localStorage.setItem('authenticated', value.toString()),
  }

  async function login(email: string, password: string, otp?: string) {
    const route = useRoute()

    const response = await $directus.login(email, password)

    const returnPath = route.query.redirect?.toString()
    const redirect = returnPath ?? '/portal'

    _loggedIn.set(true)

    setTimeout(async () => {
      await fetchUser({ fields: ['*', 'role.name'] })
      await navigateTo(nuxtApp.$localePath(redirect))
    }, 100)
  }

  async function logout() {
    const token = await $directus.getToken()

    await $directus.logout()

    user.value = null

    await clearNuxtData()
    await navigateTo(
      nuxtApp.$localePath(
        config.public?.directus?.auth?.redirect?.login || '/auth/login',
      ),
    )
  }

  async function fetchUser(parameters?: object) {
    const fields = config.public?.directus?.auth?.userFields || ['*']

    const response = await $directus.request(
      readMe({
        // @ts-ignore
        fields,
        ...parameters,
      }),
    )
    user.value = {
      ...response,
      full_name: `${response.first_name} ${response.last_name}`,
      role: slugify(response.role.name),
    } as User
  }

  async function isTokenExpired(token: string) {
    const decodedToken = jwtDecode(token) as { exp: number }
    const expirationDate = new Date(decodedToken.exp * 1000)
    return expirationDate < new Date()
  }

  return {
    _loggedIn,
    fetchUser,
    isTokenExpired,
    login,
    logout,
    user,
  }
}
